import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { settingsService } from '../services/settingsServiceAppwrite';
import { useAuth } from './AuthContextAppwrite';

const SettingsContext = createContext(undefined);

const DEFAULT_SETTINGS = {
  darkMode: false,
  compactView: false,
  showGridLines: true,
  emailNotifications: true,
  temperatureAlerts: true,
  sampleMovementAlerts: false,
  sessionTimeoutMinutes: 30, // Default to 30 minutes
  autoBackup: true,
  dataRetentionPolicy: true,
  exportData: true
};

export const SettingsProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);

  // CRITICAL FIX: Use user.id as dependency, not the entire user object
  // This prevents re-renders when other user properties change
  const userId = user?.id;

  // Load settings when userId changes
  useEffect(() => {
    if (authLoading) return;

    let mounted = true;

    const loadSettings = async () => {
      if (!userId) {
        setSettings(DEFAULT_SETTINGS);
        return;
      }

      try {
        setLoading(true);
        const userSettings = await settingsService?.getUserSettings(userId);
        
        if (mounted) {
          setSettings(userSettings || DEFAULT_SETTINGS);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        if (mounted) {
          setSettings(DEFAULT_SETTINGS);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      mounted = false;
    };
  }, [userId, authLoading]); // Only depend on userId, not entire user object

  // Handle dark mode
  useEffect(() => {
    if (settings?.darkMode) {
      document.documentElement?.classList?.add('dark');
    } else {
      document.documentElement?.classList?.remove('dark');
    }
  }, [settings?.darkMode]);

  // CRITICAL FIX: Stabilize callbacks with userId dependency only
  const updateSetting = useCallback(async (settingKey, value) => {
    if (!userId) {
      console.warn('Cannot update settings: No user logged in');
      return;
    }

    setSettings(prev => ({ ...prev, [settingKey]: value }));

    try {
      const updatedSettings = await settingsService?.updateSetting(userId, settingKey, value);
      setSettings(updatedSettings);
    } catch (error) {
      console.error('Failed to update setting:', error);
      setSettings(prev => prev);
    }
  }, [userId]); // Only userId dependency

  const refreshSettings = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const userSettings = await settingsService?.getUserSettings(userId);
      setSettings(userSettings || DEFAULT_SETTINGS);
    } catch (error) {
      console.error('Failed to refresh settings:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]); // Only userId dependency

  // CRITICAL FIX: Memoize context value with minimal dependencies
  // Only recreate when settings, loading, or callbacks change
  const value = useMemo(() => ({
    settings,
    loading,
    updateSetting,
    refreshSettings
  }), [settings, loading, updateSetting, refreshSettings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export default SettingsContext;